/* eslint-disable @typescript-eslint/no-explicit-any */
export interface Avatar {
    avatarUrl: string;
    /**
     * The time the avatar was created as ISO8601 datetime
     */
    created: string;
    /**
     * The id of the user who created this avatar
     */
    creator: string;
    gender:  string;
    /**
     * The name of the avatar
     */
    name: string;
    [property: string]: any;
}

export interface BotMessageRequest {
    message?: string;
    /**
     * Scenario data to use. Merges with but overrides data in the scenario identified in
     * sessionData.
     */
    scenarioData?: Scenario;
    /**
     * Optional session data to merge into any existing session data.
     */
    sessionData?: Session;
    /**
     * Session ID
     */
    sessionId?: string;
    [property: string]: any;
}

/**
 * Scenario data to use. Merges with but overrides data in the scenario identified in
 * sessionData.
 */
export interface Scenario {
    $schema?: string;
    /**
     * Object of actions that can be taken by the characters in the scenario
     */
    actions?: { [key: string]: ScenarioAction };
    /**
     * Map of child scenario IDs and their settings.
     */
    childScenarios?: { [key: string]: ChildScenario };
    /**
     * Object of documents uploaded by users
     */
    documents?: { [key: string]: Document };
    /**
     * Important keywords in this scenario, used for improving speech-to-text
     */
    keywords?: string[];
    /**
     * HTML content for learning material shown to users of this scenario.
     */
    learningContent?: string;
    modes?:           { [key: string]: ScenarioExchangeList };
    options?:         SettingsObject;
    /**
     * Map of all parents scenario IDs. True value means direct parent, false means indirect
     * parent.
     */
    parentScenarios?: { [key: string]: boolean };
    settings?:        Settings;
    /**
     * Map of user/org IDs that this scenario is shared with.
     */
    sharedWith?: { [key: string]: boolean };
    /**
     * Current stats for the scenario
     */
    stats?: ScenarioStats;
    /**
     * Steps that the scenario should go through, e.g. a state machine.
     */
    steps?: { [key: string]: ScenarioStep };
}

export interface ScenarioAction {
    /**
     * ID of the character taking the action, null or no value means the system
     */
    character?: string;
    /**
     * Description or additional information about the action
     */
    description?: string;
    /**
     * Name of the action
     */
    displayName?: string;
    /**
     * What to do in the action. Multiple can be set together but some may override each other.
     * Some are also not directly applicable to player characters.
     */
    do?: Do;
    /**
     * Optional conditions that all must be true for the action to be taken
     */
    ifAll?: IfAll;
    /**
     * If the action should be counted as a scenario goal or not. Defaults to true.
     */
    isGoal?: boolean;
    /**
     * If the action should be considered as a player action or not. Defaults to false.
     */
    isPlayer?: boolean;
    /**
     * Default order should be ascending. Can also be used as a priority in case multiple
     * actions are triggered at the same time.
     */
    order?: number;
    [property: string]: any;
}

/**
 * What to do in the action. Multiple can be set together but some may override each other.
 * Some are also not directly applicable to player characters.
 */
export interface Do {
    /**
     * Optional metric to achieve as a goal, intended for players. This is normally measured
     * after the session but it could theoretically also be measured whenever the metric is
     * achieved.
     */
    achieveMetric?: AchieveMetric;
    /**
     * Instructs the bot to take any supported action.
     */
    callFunction?: CallFunction;
    /**
     * Change the speaker mode of the scenario to the given mode.
     */
    changeMode?: ChangeMode;
    /**
     * The gesture that the character should perform. Currently only relevant for bots.
     */
    gesture?: string;
    /**
     * If true, the character should leave the session at this point (which possibly ends the
     * session)
     */
    leave?: boolean;
    /**
     * The mood that the should be shown (together with other parts of this action). Currently
     * only relevant for bots.
     */
    mood?: Mood;
    /**
     * An action to take described as a generic prompt, e.g. exhibit some behavior in the
     * conversation.
     */
    prompt?: string;
    /**
     * An array of possible things to say (exactly).
     */
    sayExactly?: string[];
    [property: string]: any;
}

/**
 * Optional metric to achieve as a goal, intended for players. This is normally measured
 * after the session but it could theoretically also be measured whenever the metric is
 * achieved.
 */
export interface AchieveMetric {
    /**
     * The key of the metric to achieve
     */
    key?: string;
    /**
     * The value to achieve ends at this value. It not given, max value is assumed to be
     * positive infinity.
     */
    maxValue?: number;
    /**
     * The value to achieve starts at this value. If not given, min value is assumed to be
     * negative infinity.
     */
    minValue?: number;
    [property: string]: any;
}

/**
 * Instructs the bot to take any supported action.
 */
export interface CallFunction {
    /**
     * The specific arguments to the function, if any.
     */
    args?: { [key: string]: any };
    /**
     * The function to call, must match available function IDs
     */
    function?: Function;
    /**
     * A human readable description of the arguments to the function, to be interpreted by an
     * LLM.
     */
    prompt?: string;
    [property: string]: any;
}

/**
 * The function to call, must match available function IDs
 */
export enum Function {
    ChangeScenarioSetting = "change_scenario_setting",
    PromptPlayer = "prompt_player",
}

/**
 * Change the speaker mode of the scenario to the given mode.
 */
export enum ChangeMode {
    ListenOnly = "listenOnly",
    SpeakOnSilence = "speakOnSilence",
}

/**
 * The mood that the should be shown (together with other parts of this action). Currently
 * only relevant for bots.
 */
export enum Mood {
    Bored = "bored",
    Happy = "happy",
    InDoubt = "inDoubt",
    Interested = "interested",
    Irritated = "irritated",
    Neutral = "neutral",
    Unaffected = "unaffected",
}

/**
 * Optional conditions that all must be true for the action to be taken
 */
export interface IfAll {
    /**
     * The action ID that this action must be triggered as a response to.
     */
    afterAction?: string;
    /**
     * A prompt description to return as truthy for this action to be taken
     */
    prompt?: string;
    /**
     * Time since start. A negative value means at the end of the session or when the character
     * leaves.
     */
    timeSince?: number;
    [property: string]: any;
}

export interface ChildScenario {
    /**
     * Overrides the child scenario description when displayed in the parent scenario.
     */
    description?: string;
    /**
     * Overrides the child scenario displayName when displayed in the parent scenario.
     */
    displayName?: string;
    order?:       number;
    [property: string]: any;
}

export interface Document {
    /**
     * Byline of document
     */
    byline?: string;
    /**
     * Total number of child documents a crawl is expected to find.
     */
    childCount?: number;
    /**
     * Total number of child documents a crawl actually completed.
     */
    completedChildCount?: number;
    /**
     * String encoded content of document
     */
    content?: string;
    /**
     * URL of an image to represent the document
     */
    coverImage?: string;
    /**
     * Date-like string of when document was created
     */
    created?: string;
    /**
     * Error message (robots blocked and page or crawl-level error).
     */
    docError?: string;
    /**
     * Excerpt of document
     */
    excerpt?: string;
    /**
     * If the document should be considered in scenario
     */
    isActive?: boolean;
    /**
     * Language of document
     */
    lang?: string;
    /**
     * Mime type of the document
     */
    mimeType?: string;
    /**
     * Name of the document or file
     */
    name?: string;
    /**
     * Namespace of document
     */
    namespace?: string;
    /**
     * If this document is a child of another (e.g a folder, a crawl), this points to that ID.
     */
    parentId?: string;
    /**
     * If the document should be shown to players
     */
    showToPlayers?: boolean;
    /**
     * Status of the document loading or crawling process
     */
    status?: DocumentStatus;
    /**
     * HTTP status code received when fetching a crawled document
     */
    statusCode?: number;
    /**
     * Title of document
     */
    title?: string;
    /**
     * Time document was last fetched as ISO8601 datetime.
     */
    updated?: string;
    /**
     * Path to the file or URL of the document
     */
    uri?: string;
    [property: string]: any;
}

/**
 * Status of the document loading or crawling process
 */
export enum DocumentStatus {
    Cancelled = "cancelled",
    Completed = "completed",
    Failed = "failed",
    Loading = "loading",
    Pending = "pending",
    Scraping = "scraping",
}

export interface ScenarioExchangeList {
    exchanges?: { [key: string]: Exchange };
    /**
     * Introduction that the bot will say at the beginning of a mode.
     */
    modeIntroduction?: string;
    /**
     * Default order should be ascending
     */
    order?: number;
    [property: string]: any;
}

export interface Exchange {
    description?: string;
    displayName?: string;
    intent?:      string;
    /**
     * If this exchange should be counted as a goal or not. Defaults to true.
     */
    isGoal?: boolean;
    lines:   ScenarioLine[];
    /**
     * Default order should be ascending.
     */
    order: number;
}

export interface ScenarioLine {
    /**
     * The key of the character speaking the line (defined in #/settings/characters).
     */
    character?: string;
    /**
     * Add a Suggestion Event to the array every time a user adds a set of suggestions (or when
     * rejecting all by requesting a new set of suggestions)
     */
    suggestionStats?: { [key: string]: SuggestionEvent };
    variants?:        LineVariant[];
    [property: string]: any;
}

export interface SuggestionEvent {
    /**
     * The suggested variants that where accepted (added to the scenario)
     */
    acceptedVariants?: string[];
    /**
     * Denotes where the suggestion originated from.
     */
    addedFrom?: AddedFrom;
    /**
     * Time scenario was created as ISO8601 datetime.
     */
    created?: string;
    /**
     * The variants as they were before this suggestion event.
     */
    inputVariants?: string[];
    /**
     * The relevant preceding bot variants that was used for this suggestion event.
     */
    precedingVariants?: string[];
    /**
     * The suggested variants that where rejected (not added)
     */
    rejectedVariants?: string[];
    /**
     * The scenario description that was used for this suggestion event.
     */
    scenarioDescription?: string;
    /**
     * The topic/exchange description that was used for this suggestion event.
     */
    topicDescription?: string;
    /**
     * The id of the user that requested suggested variants.
     */
    user?: string;
    [property: string]: any;
}

/**
 * Denotes where the suggestion originated from.
 *
 * Denotes where the line variant was added from, e.g. the editor wrote it, it was taken
 * from a played session or it was generated.
 */
export enum AddedFrom {
    Editor = "editor",
    Generator = "generator",
    PlayerSession = "playerSession",
}

export interface LineVariant {
    /**
     * Denotes where the line variant was added from, e.g. the editor wrote it, it was taken
     * from a played session or it was generated.
     */
    addedFrom?: AddedFrom;
    /**
     * Example audio structure: 'sv-SE/male/Mattias-A: bra,-fortsätt_501f3eb0.wav'
     */
    audio?: { [key: string]: { [key: string]: { [key: string]: string } } };
    mood?:  Mood;
    /**
     * States for the answer mode if the reply should be a negative reply or not.
     */
    negativeReply?: boolean;
    text?:          string;
    [property: string]: any;
}

/**
 * A settings object refers to user facing settings (although not all settings necessarily
 * are shown to every user). To be included in scopes build/env/org/user/scenario.
 */
export interface SettingsObject {
    /**
     * If true, emoji will be allowed in the LLM transcripts, otherwise it will be filtered out.
     * Note that the prompt controls the extent to which emoji are used.
     */
    agentAllowEmojiOutput?: boolean;
    /**
     * If set to a number > 0, the agent will wait this many seconds before responding.
     */
    agentArtificialDelay?:   number;
    agentCanInterruptsUser?: boolean;
    agentFrequencyPenalty?:  number;
    agentMaxResponseTokens?: number;
    agentModelName?:         string;
    agentPresencePenalty?:   number;
    agentProvider?:          string;
    agentRatingPrompt?:      string;
    /**
     * If true, the output of the agent may be rich text.
     */
    agentRichTextOutput?:  boolean;
    agentRolePrefixMode?:  AgentRolePrefixMode;
    agentSendFillerAudio?: boolean;
    agentSystemPrompt?:    string;
    agentTemperature?:     number;
    /**
     * Which tools the AI actor may call during a session. Each key is a tool name from
     * tools/agent-tools.json; tools marked alwaysEnabled there are on regardless of this
     * setting. Unknown names are ignored by the backend.
     */
    agentTools?:            { [key: string]: boolean };
    agentUserCanInterrupt?: boolean;
    /**
     * Similarity (%) threshold from which a line is considered a repetition
     */
    alreadySaidThreshold?: number;
    /**
     * URL to a logo to use for the app (if not default)
     */
    appLogo?: string;
    /**
     * How to name the app for users (if not default)
     */
    appName?: string;
    /**
     * Inform users about sharing and ask for consent when starting chats
     */
    askChatConsent?: boolean;
    /**
     * If true, this scenario is an assessment scenario.
     */
    assessmentMode?: boolean;
    /**
     * Which agent providers are available for use in scenarios.
     */
    availableAgentProviders?: { [key: string]: boolean };
    availableAvatars?:        { [key: string]: boolean };
    availableEnvironments?:   { [key: string]: boolean };
    availableIntegrations?:   { [key: string]: boolean };
    availableLocales?:        { [key: string]: boolean };
    /**
     * Each key corresponds to a rating key. If value is true, the rating should be present and
     * editable on sessions, otherwise it should not be available.
     */
    availableRatings?:   { [key: string]: boolean };
    availableScenarios?: { [key: string]: boolean };
    /**
     * Each key represents a unique tag users in this client can choose. UI should forbid tags
     * that when slugified becomes: all, admins.
     */
    availableScenarioTags?: { [key: string]: boolean };
    /**
     * Each key represents a unique tag users in this client can choose. UI should forbid tags
     * that when slugified becomes: all, admins.
     */
    availableUserTags?:       { [key: string]: boolean };
    availableVoiceProviders?: { [key: string]: boolean };
    /**
     * Selects which backend target alias to use. If unset or set to default, the app chooses
     * the standard for the current environment.
     */
    backendChannel?: BackendChannel;
    /**
     * The ratio of sessions that should go to the backup server.
     */
    backupServerRatio?: number;
    boredomHigh?:       number;
    boredomLow?:        number;
    boredomVeryLow?:    number;
    /**
     * Controls the visibility of the chat input field. Users can toggle it unless disabled.
     */
    chatInputVisibility?: ChatVisibility;
    /**
     * Controls the visibility of the chat log. Users can toggle it unless disabled.
     */
    chatLogVisibility?:           ChatVisibility;
    confusionHigh?:               number;
    confusionLow?:                number;
    confusionVeryLow?:            number;
    conversationIdleTimeSeconds?: number;
    /**
     * Custom server URL for the backend. Only used if Backend Channel is set to Custom.
     */
    customServerUrl?:    string;
    dataRetentionDays?:  number;
    debugMode?:          boolean;
    dialogueAppChannel?: DialogueAppChannel;
    /**
     * If true, disable chat log in this scenario.
     */
    disableChatLog?: boolean;
    /**
     * If true, disable text input in chat in this scenario.
     */
    disableTextInput?:    boolean;
    displayErrorsInChat?: boolean;
    /**
     * If true, the scenario editor offers a 'copy and translate to <language>' action that
     * machine-translates a scenario copy into a target locale. Per-organization feature flag
     * (translation spends an LLM call).
     */
    enableTranslateCopy?: boolean;
    engagementHigh?:      number;
    engagementLow?:       number;
    engagementVeryLow?:   number;
    /**
     * If false, saves sessions to the database. If true, will consider it ephemeral.
     */
    ephemeralSessions?: boolean;
    /**
     * Number of fillers in session above which the user will get a weak warning
     */
    fillersMany?: number;
    /**
     * Number of fillers in session above which the user will get a strong warning
     */
    fillersTooMany?: number;
    /**
     * Tells which channel the user prefers. The user may still enter the app via another
     * channel.
     */
    frontendChannel?:    FrontendChannel;
    frustrationHigh?:    number;
    frustrationLow?:     number;
    frustrationVeryLow?: number;
    /**
     * At what proportion of time a user is looking away is too much
     */
    gazeNotLooking?: number;
    /**
     * If less than this proportion of time, user uses too few gestures
     */
    gesturesTooFew?: number;
    /**
     * A modifier to the goal scoring. Easy means the goals should be easier to achieve, hard
     * means they should be harder to achieve.
     */
    goalScoringModifier?: GoalScoringModifier;
    /**
     * At what proportion of time of head moving is considered too much (too shaky, nervous)
     */
    headMovementsShaky?: number;
    /**
     * At what proportion of time of head moving is considered too little (too still)
     */
    headMovementsStill?: number;
    /**
     * Number of seconds above which the user hesitates for too long
     */
    hesitationLengthSeconds?: number;
    /**
     * Automatically sign out the user after this many minutes without activity. Set to 0 to
     * disable.
     */
    inactivityLogoutMinutes?: number;
    lipsyncProvider?:         LipsyncProvider;
    /**
     * Can be used to reduce or increase strength of lipsync.
     */
    lipsyncStrength?:        number;
    microphoneStartUnmuted?: boolean;
    /**
     * Minimum number of words the user must speak to interrupt the AI. Only enforced while the
     * AI is speaking. Applies to all strategies.
     */
    minWordsForInterruption?:                  number;
    multilingualScenarioFeature?:              boolean;
    outOfCharacterZeroShotRoleDetectorPrompt?: string;
    /**
     * Mean derivative of pitch (Hz/s) below which the user talks too monotonous
     */
    pitchMeanDerivativeThreshold?:           number;
    preferredMicrophone?:                    string;
    preferredUILocale?:                      PreferredUILocale;
    scenarioDocumentCrawlingFeature?:        boolean;
    scenarioDocumentsFeature?:               boolean;
    scenarioDocumentsMaxCharactersInPrompt?: number;
    scenarioDocumentsPipeline?:              ScenarioDocumentsPipeline;
    /**
     * Each key is a scenario, and the string value is an identifier for which scenario group
     * it's in, if any.
     */
    scenarioGrouping?:      { [key: string]: string };
    scenarioOrder?:         { [key: string]: number };
    scenarioWizardFeature?: boolean;
    sendActivityEmail?:     boolean;
    sendDiagnostics?:       boolean;
    /**
     * What ratio of errors should be sent to Sentry. 0 to disable
     */
    sentryErrorSampleRate?:          number;
    sentryReplaysSessionSampleRate?: number;
    /**
     * What ratio of performance reporting to send to Sentry. 0 to disable
     */
    sentryTracingSampleRate?: number;
    /**
     * Prompt to use for generating a session summary.
     */
    sessionSummaryPrompt?:     string;
    sessionTimeoutSeconds?:    number;
    showSlidesFromStart?:      boolean;
    showSubtitles?:            boolean;
    skillLevelToConsiderDone?: number;
    skipIceServers?:           boolean;
    /**
     * This setting controls whether to skip sending out an email when a session is shared with
     * new people or a sharing is modified. It should not affect whether the owner of the
     * session (e.g. the player) receives an email or not.
     */
    skipSendingSharedWithEmail?: boolean;
    /**
     * Smart strategy only. Milliseconds of audio before detected speech onset to include in the
     * ML analysis buffer.
     */
    smartTurnPreSpeechMs?: number;
    /**
     * Smart strategy only. Seconds of silence before the ML model analyzes if the turn is
     * complete. Used when turnTakeDelay is 'manual'.
     */
    smartTurnStopSecs?:                        number;
    stepScenarioFeature?:                      boolean;
    supportBotFeature?:                        boolean;
    synthesizerModelName?:                     string;
    synthesizerPitch?:                         number;
    synthesizerProvider?:                      string;
    synthesizerRate?:                          number;
    synthesizerVoice?:                         string;
    transcriberInitialSilenceTimeoutMs?:       number;
    transcriberModelName?:                     string;
    transcriberProvider?:                      string;
    transcriberSegmentationSilenceTimeoutMs?:  number;
    transcriberWaitForHumanContinuingSeconds?: number;
    transportType?:                            TransportType;
    /**
     * Safety timeout in seconds. If the user starts speaking but no stop strategy triggers
     * within this time (e.g., STT hangs or ML model doesn't respond), the turn is force-ended.
     * Prevents the user from getting stuck in a 'speaking' state. Applies to all strategies.
     */
    turnStopTimeout?: number;
    /**
     * Preset for the stop timing delay. Applies to silenceBased and smart strategies, but maps
     * to different underlying values. 'manual' uses vadStopSecs (silenceBased) or
     * smartTurnStopSecs (smart) directly.
     */
    turnTakeDelay?: TurnTakeDelay;
    /**
     * Selects the turn-taking stop strategy. 'silenceBased' uses silence detection.
     * 'transcription' waits for speech recognition confirmation. 'smart' (default) uses
     * ML-based analysis.
     */
    turnTakingStrategy?: TurnTakingStrategy;
    /**
     * Transcription strategy only. Seconds to wait for the user to resume speaking after a
     * pause, before ending their turn.
     */
    userSpeechTimeout?: number;
    /**
     * SileroVAD confidence threshold for speech detection. Applies to both standard and smart
     * strategies. Higher = stricter (less sensitive to noise), lower = more sensitive.
     */
    vadConfidence?: number;
    /**
     * Applies to both strategies. Consecutive seconds of speech required before VAD confirms
     * the user is speaking.
     */
    vadStartSecs?: number;
    /**
     * Silence-based strategy only. Seconds of silence before ending the user's turn. Used when
     * turnTakeDelay is 'manual'.
     */
    vadStopSecs?: number;
    /**
     * Volume (dB) above which the user talks too loud
     */
    volumeTooHigh?: number;
    /**
     * Volume (dB) below which the user talks too quiet
     */
    volumeTooLow?:     number;
    webAvatarFeature?: boolean;
    /**
     * Minimal number of words below which no event is raised for the word rate
     */
    wordRateMinimalNumberOfWords?: number;
    /**
     * Word rate (word/min) above which the user talks much too fast
     */
    wordRateMuchTooFast?: number;
    /**
     * Word rate (word/min) under which the user talks much too slow
     */
    wordRateMuchTooSlow?: number;
    /**
     * Word rate (word/min) above which the user talks too fast
     */
    wordRateTooFast?: number;
    /**
     * Word rate (word/min) under which the user talks too slow
     */
    wordRateTooSlow?: number;
}

export enum AgentRolePrefixMode {
    Default = "default",
    DisplayName = "displayName",
    PromptRole = "promptRole",
}

/**
 * Selects which backend target alias to use. If unset or set to default, the app chooses
 * the standard for the current environment.
 */
export enum BackendChannel {
    Custom = "custom",
    Default = "default",
    PipecatAlpha = "pipecatAlpha",
    PipecatBeta = "pipecatBeta",
    PipecatDev = "pipecatDev",
    PipecatDevAlpha = "pipecatDevAlpha",
    PipecatStable = "pipecatStable",
    VocodeAlpha = "vocodeAlpha",
    VocodeAlphaBackup = "vocodeAlphaBackup",
    VocodeBeta = "vocodeBeta",
    VocodeBetaBackup = "vocodeBetaBackup",
}

/**
 * Controls the visibility of the chat input field. Users can toggle it unless disabled.
 *
 * Controls the visibility of the chat log. Users can toggle it unless disabled.
 */
export enum ChatVisibility {
    Disabled = "disabled",
    Hidden = "hidden",
    Shown = "shown",
}

export enum DialogueAppChannel {
    Alpha = "alpha",
    Beta = "beta",
    BetaBackup = "betaBackup",
    Release = "release",
}

/**
 * Tells which channel the user prefers. The user may still enter the app via another
 * channel.
 */
export enum FrontendChannel {
    Alpha = "alpha",
    Beta = "beta",
    Stable = "stable",
}

/**
 * A modifier to the goal scoring. Easy means the goals should be easier to achieve, hard
 * means they should be harder to achieve.
 */
export enum GoalScoringModifier {
    Easy = "easy",
    Hard = "hard",
    Normal = "normal",
}

export enum LipsyncProvider {
    Ovr = "ovr",
}

export enum PreferredUILocale {
    EnUS = "en-US",
}

export enum ScenarioDocumentsPipeline {
    Gemini = "gemini",
    None = "none",
    Unstructured = "unstructured",
}

export enum TransportType {
    Daily = "daily",
    Webrtc = "webrtc",
    Websocket = "websocket",
}

/**
 * Preset for the stop timing delay. Applies to silenceBased and smart strategies, but maps
 * to different underlying values. 'manual' uses vadStopSecs (silenceBased) or
 * smartTurnStopSecs (smart) directly.
 */
export enum TurnTakeDelay {
    Long = "long",
    Manual = "manual",
    Normal = "normal",
    Short = "short",
}

/**
 * Selects the turn-taking stop strategy. 'silenceBased' uses silence detection.
 * 'transcription' waits for speech recognition confirmation. 'smart' (default) uses
 * ML-based analysis.
 */
export enum TurnTakingStrategy {
    SilenceBased = "silenceBased",
    Smart = "smart",
    Transcription = "transcription",
}

export interface Settings {
    /**
     * Parseable string that sets non-default camera position X, Y and Z and FoV.
     */
    cameraPosition?: string;
    /**
     * Need to have at least one character with role bot, one with teacher.
     */
    characters?: { [key: string]: Character };
    coverImage?: string;
    /**
     * Time scenario was created as ISO8601 datetime.
     */
    created?: string;
    /**
     * Shows where the scenario was created from.
     */
    createdFrom?: CreatedFrom;
    /**
     * The id of the organization where this scenario was created.
     */
    creatingOrganization?: string;
    /**
     * The id of the user that created the scenario.
     */
    creator?: string;
    /**
     * Sessions created with this scenario should automatically be shared with as per this map.
     * These settings are not possible to override or change by a user on a session. This map
     * would typically contain the scenario's creator or organization_admins.
     */
    defaultSessionsSharedWith?: { [key: string]: boolean };
    description?:               string;
    displayName?:               string;
    /**
     * If this is given, all document queries will additionally have these queries, encoded as
     * URL query parameters
     */
    documentQueryArguments?: string;
    environment?:            string;
    /**
     * The complete prompt template that will override the config.ini defined prompt, just for
     * this scenario. It means it should include template variables such as $messageHistory.
     */
    fullPrompt?: string;
    /**
     * The scenario is currently generating some content so may not work correctly yet.
     */
    generating?: boolean;
    /**
     * Whether this scenario should report progress to Happyr
     */
    happyrIntegration?: boolean;
    /**
     * The ID of the step to start at. If not given, the step with the lowest order value will
     * be used.
     */
    initialStep?: string;
    /**
     * An ID string that can be found under /invites/:id
     */
    invite?: string;
    /**
     * A map of invite IDs created for this scenario
     */
    invites?: { [key: string]: boolean };
    /**
     * The id of the user that last edited the scenario.
     */
    lastEditedBy?: string;
    /**
     * If this scenario integrates with Learnifier, provide the project ID here.
     */
    learnifierProjectId?: string;
    /**
     * If this scenario integrates with Learnster, provide the session ID here.
     */
    learnsterSessionId?: string;
    /**
     * If this scenario integrates with Learnster, provide the vendor ID here.
     */
    learnsterVendorId?: string;
    /**
     * Primary locale that this scenario is created for. BCP-47 language tag.
     */
    locale?: string;
    /**
     * List of alternative  BCP-47 locales that this scenario supports. May or may not include
     * also the primary locale.
     */
    locales?: string[];
    /**
     * DEPRECATED Who is the AI Avatar? A description of the AI Avatar's personality that will
     * be used in prompt autotune and prompt template to GPT.
     */
    personalityPrompt?: string;
    /**
     * A description of what should happen in the scenario, sent as part of the prompt template
     * to GPT
     */
    prompt?: string;
    /**
     * Introduction to the scenario that the bot will say at the beginning of a session.
     */
    scenarioIntroduction?: string;
    /**
     * Message to show to users when playing this scenario, describing who will get data shared
     * with them.
     */
    sharedWithMessage?: string;
    slides?:            string[];
    /**
     * Survey questions for the scenario. The key is an id of the question (that needs to be
     * unique and the text for it needs to be in the translations).
     */
    surveyQuestions?: { [key: string]: string };
    /**
     * Time scenario was last updated as ISO8601 datetime.
     */
    updated?: string;
    /**
     * Flag that is true if there is something that could impact the quality of the scenario
     * (not trained, variants poorly detected, too few variants).
     */
    warning?: boolean;
    /**
     * Dictionary of intents and their warnings, coming from training
     */
    warnings?: { [key: string]: any };
    [property: string]: any;
}

export interface Character {
    /**
     * Now means a unique identifier of the avatar. Either a new RPM style URL, or a shorthand
     * for preloaded avatar or a gender for default gendered avatar.
     */
    avatar?:      string;
    displayName?: string;
    gender?:      Gender;
    /**
     * Who is the AI Avatar? A description of the AI Avatar's personality that will be used in
     * conversation. Best if short and to the point.
     */
    personalityPrompt?: string;
    /**
     * The role the character will take on during a GPT scenario
     */
    promptRole?: string;
    role:        Role;
    /**
     * Default named position to spawn on in the environment
     */
    spawnPosition?: string;
    voiceName?:     string;
}

export enum Gender {
    Female = "female",
    Male = "male",
    Other = "other",
}

export enum Role {
    Bot = "bot",
    Extra = "extra",
    Player = "player",
    Teacher = "teacher",
}

/**
 * Shows where the scenario was created from.
 */
export enum CreatedFrom {
    Editor = "editor",
    Wizard = "wizard",
}

/**
 * Current stats for the scenario
 */
export interface ScenarioStats {
    /**
     * Number of unique users that have had a session with this scenario in the last 30 days
     */
    activeUsers?: number;
    /**
     * Average app recommendation evaluation for all sessions that got an evaluation, [0-1].
     * Counted by averageAppRecommend.
     */
    averageAppRecommend?: number;
    /**
     * Average bot performance evaluation for all sessions that got an evaluation, [0-1].
     * Counted by countBotPerformance.
     */
    averageBotPerformance?: number;
    /**
     * Average goal score for all sessions with this scenario, [0-1]. Counted by countGoalScore.
     */
    averageGoalScore?: number;
    /**
     * Average of latest scenario skill level for all users who have played this scenario
     */
    averageLatestSkill?: number;
    /**
     * Count occurrence for each app recommend score key (1, 2, 3, 4, 5.)
     */
    countAppRecommend?: { [key: string]: number };
    /**
     * Count occurrence for each bot performance score key (1, 2, 3, 4, 5.)
     */
    countBotPerformance?: { [key: string]: number };
    /**
     * Count which decile average goal score fall in (10%, 20%, 30%, 40%, 50%...)
     */
    countGoalScore?: { [key: string]: number };
    /**
     * Count which decile average skill fall in (10%, 20%, 30%, 40%, 50%...)
     */
    countSkill?: { [key: string]: number };
    /**
     * The first time a session was played with this scenario
     */
    firstSessionDateTime?: string;
    /**
     * The most recent time a session was played with this scenario
     */
    lastSessionDateTime?: string;
    /**
     * Total accumulated playtime, in ms, for all sessions with this scenario, regardless of
     * user.
     */
    playtimeTotal?: number;
    /**
     * Number of sessions with this scenario that have been analyzed for goals and AI coach
     */
    sessionsAnalyzed?: number;
    /**
     * Number of sessions with this scenario that ended normally, e.g. not aborted
     */
    sessionsEnded?: number;
    /**
     * Number of sessions with this scenario that have received at least one user or admin
     * feedback, e.g. thumbs up/down
     */
    sessionsFeedbacked?: number;
    /**
     * Number of sessions with this scenario that have been marked as finalized
     */
    sessionsFinalized?: number;
    /**
     * Number of sessions where a user has started speaking to the bot
     */
    sessionsPlayed?: number;
    /**
     * Number of sessions with this scenario that have been marked as reviewed by an admin or
     * manager
     */
    sessionsRated?: number;
    /**
     * Number of sessions with this scenario that has been started
     */
    sessionsStarted?: number;
    /**
     * Array of skill levels over time, e.g. for graphing. Should be kept at a maximum length by
     * removing less useful entries.
     */
    skillHistory?: PurpleSkillHistory[];
    [property: string]: any;
}

export interface PurpleSkillHistory {
    datetime: string;
    value:    number;
    [property: string]: any;
}

/**
 * Describes a single step or state in the chat script.
 */
export interface ScenarioStep {
    /**
     * Context variables to set for this step.
     */
    context?: Context;
    /**
     * Events to add to session upon entering this step.
     */
    entry?: SessionEvent[];
    /**
     * Events to add to session upon exiting this step.
     */
    exit?: SessionEvent[];
    /**
     * Possible scenario functions to call in this step.
     */
    functions?: ScenarioFunction[];
    /**
     * Default order should be ascending. Lowest order is the first step.
     */
    order?: number;
    /**
     * Human readable name of the step
     */
    title?: string;
}

/**
 * Context variables to set for this step.
 */
export interface Context {
    prompt?: string;
}

export interface SessionEvent {
    /**
     * Arguments for events like function calls.
     */
    arguments?: { [key: string]: any };
    /**
     * Relative path to the audio file that represents this event
     */
    audio?: string;
    /**
     * ID of the character that was involved with this event
     */
    character?: string;
    /**
     * Whether the event was cut off by the next event or some other reason
     */
    cutOff?: boolean;
    /**
     * String data, such as an CHOICE_INPUT id or a base64 encoded image.
     */
    data?: string;
    /**
     * Duration of the event in milliseconds
     */
    duration?:   number;
    evaluation?: { [key: string]: SessionRating };
    /**
     * What kind of event this is. Tool calls are recorded as TOOL_CALL and TOOL_RESULT, with
     * executedBy naming the side that ran the tool. CLIENT_TOOL and CLIENT_TOOL_USED are the
     * older form, kept readable for sessions recorded before that and no longer written.
     */
    eventType: EventType;
    /**
     * ID of the exchange (from session scenario or internal scenarios)
     */
    exchangeId?: string;
    /**
     * For TOOL_CALL and TOOL_RESULT, which side ran the tool.
     */
    executedBy?: ToolExecutor;
    /**
     * Feedback on the event itself, not a rating, used for suggesting better texts.
     */
    feedback?: Feedback;
    /**
     * The gesture that the character performed during this event.
     */
    gesture?: string;
    /**
     * A unique id for this event within the session.
     */
    id?: string;
    /**
     * Input object for the event that represents data input.
     */
    input?: Input;
    /**
     * Deprecated just to support old websocket events. Should not be saved into the database.
     */
    is_final?: boolean;
    /**
     * The mode that is active during this event
     */
    mode?: Mode;
    mood?: Mood;
    /**
     * A unique name for the function or template that generated this event, and also the name
     * data from this event can be stored under. Replaces exchangeId
     */
    name?: string;
    /**
     * Prompt or source data that was used to generate this event, if applicable
     */
    prompt?: string;
    /**
     * ID of the event that this event is a reply to, if applicable
     */
    replyTo?: string;
    /**
     * Role of the sender of this event
     */
    role?: SenderRole;
    /**
     * A flat object where each key is a snake_case name for a stat or metric about the session.
     */
    stats?: { [key: string]: number };
    /**
     * Represents the lifecycle of an event. 'created' indicates the event was initiated but not
     * confirmed by the server. 'confirmed' means the server has acknowledged the event.
     * 'inProgress' shows the event is ongoing (e.g., typing or generating). 'final' marks the
     * event as complete with no further changes. 'evaluated' indicates the event has been
     * analyzed by AI for additional metrics.
     */
    status?: EventStatus;
    /**
     * ID of the step the scenario was in when this event happened
     */
    stepId?: string;
    /**
     * Text representation of this event, e.g. a transcription or message.
     */
    text?: string;
    /**
     * Milliseconds since the session created timestamp.
     */
    timeSinceStart?: number;
    /**
     * Milliseconds since epoch. Should replace timeSinceStart in the future.
     */
    timestamp?: number;
    /**
     * Public (signed) url that may exist for signed resources.
     */
    url?: string;
    /**
     * Some numeric value attached to this event, e.g for an animation.
     */
    value?: number;
}

export interface SessionRating {
    /**
     * Custom data point to show that was used to deduce the rating, e.g pitch in Hz.
     */
    dataPoint?: number;
    /**
     * Custom data text, can be used for e.g. transcribed text.
     */
    dataText?: string;
    feedback?: Feedback;
    /**
     * Key that identifies the rating, e.g. 'bad', 'ok' or 'good'
     */
    ratingKey?: string;
    ratings?:   { [key: string]: RatingData };
    /**
     * A score value to accompany the key, typically between 0.0 to 1.0.
     */
    ratingScore?: number;
    [property: string]: any;
}

/**
 * Feedback on the event itself, not a rating, used for suggesting better texts.
 */
export interface Feedback {
    /**
     * A custom user comment.
     */
    comment?: string;
    /**
     * When this feedback was saved (as ISO8601 datetime)
     */
    created: string;
    /**
     * A proposed rating key from the user.
     */
    suggestedKey?: string;
    /**
     * A proposed score from the user.
     */
    suggestedScore?: number;
    [property: string]: any;
}

/**
 * Represents one set of rating data points from one source. Key should be a global id that
 * identifies the source.
 */
export interface RatingData {
    /**
     * User comment on how this data was produced
     */
    comment?: string;
    /**
     * When this dataPoint was created (as ISO8601 datetime)
     */
    created: string;
    /**
     * Custom metric or data point to that was used to deduce the rating, e.g pitch in Hz,
     * fillers/s etc.
     */
    metric?: number;
    /**
     * Key that identifies the rating, e.g. 'bad', 'ok' or 'good'
     */
    ratingKey?: string;
    /**
     * A score value to accompany the key, typically normalized between 0.0 to 1.0.
     */
    ratingScore?: number;
    /**
     * The type of source
     */
    sourceType?: SourceType;
    /**
     * Custom data text, can be used for e.g. transcribed text.
     */
    text?: string;
    [property: string]: any;
}

/**
 * The type of source
 */
export enum SourceType {
    Admin = "admin",
    Model = "model",
    User = "user",
}

/**
 * What kind of event this is. Tool calls are recorded as TOOL_CALL and TOOL_RESULT, with
 * executedBy naming the side that ran the tool. CLIENT_TOOL and CLIENT_TOOL_USED are the
 * older form, kept readable for sessions recorded before that and no longer written.
 */
export enum EventType {
    BotLine = "BOT_LINE",
    ChoiceInput = "CHOICE_INPUT",
    ClientTool = "CLIENT_TOOL",
    ClientToolUsed = "CLIENT_TOOL_USED",
    ContextUpdated = "CONTEXT_UPDATED",
    ErrorMessage = "ERROR_MESSAGE",
    Event = "EVENT",
    ImageInput = "IMAGE_INPUT",
    InputAnswer = "INPUT_ANSWER",
    MoodChanged = "MOOD_CHANGED",
    NavigateURL = "NAVIGATE_URL",
    NumberInput = "NUMBER_INPUT",
    PlayerLine = "PLAYER_LINE",
    SessionAnalyzed = "SESSION_ANALYZED",
    SessionEnded = "SESSION_ENDED",
    SessionFeedbacked = "SESSION_FEEDBACKED",
    SessionFinalized = "SESSION_FINALIZED",
    SessionRated = "SESSION_RATED",
    SessionStarted = "SESSION_STARTED",
    SettingsUpdated = "SETTINGS_UPDATED",
    StepChanged = "STEP_CHANGED",
    TextInput = "TEXT_INPUT",
    ToolCall = "TOOL_CALL",
    ToolResult = "TOOL_RESULT",
    URLNavigated = "URL_NAVIGATED",
    VideoRecorded = "VIDEO_RECORDED",
}

/**
 * For TOOL_CALL and TOOL_RESULT, which side ran the tool.
 */
export enum ToolExecutor {
    Client = "client",
    Service = "service",
    Worker = "worker",
}

/**
 * Input object for the event that represents data input.
 */
export interface Input {
    /**
     * Maximum value for NUMBER_INPUT, maximum length for TEXT_INPUT or maximum bytes for
     * IMAGE_INPUT
     */
    max?: number;
    /**
     * Minimum value for NUMBER_INPUT, minimum length for TEXT_INPUT or minimum bytes for
     * IMAGE_INPUT
     */
    min?:     number;
    options?: Option[];
    /**
     * Regex pattern for TEXT_INPUT
     */
    pattern?: string;
    /**
     * Step size for NUMBER_INPUT. An integer step implies an integer number, otherwise a
     * floating point.
     */
    step?: number;
    /**
     * A string denoting the style or class of the input, to allow different styles of e.g. a
     * CHOICE_INPUT as dropdown or buttons, a NUMBER_INPUT as star rating or range slider
     */
    style?: string;
}

/**
 * Options to present in a CHOICE_INPUT
 */
export interface Option {
    id:    string;
    title: string;
    [property: string]: any;
}

/**
 * The mode that is active during this event
 */
export enum Mode {
    Answer = "answer",
    Ask = "ask",
    Chitchat = "chitchat",
    Done = "done",
    Explain = "explain",
    Free = "free",
    Generic = "generic",
    Script = "script",
}

/**
 * Role of the sender of this event
 */
export enum SenderRole {
    Bot = "bot",
    Human = "human",
    Player = "player",
    System = "system",
    Viewer = "viewer",
}

/**
 * Represents the lifecycle of an event. 'created' indicates the event was initiated but not
 * confirmed by the server. 'confirmed' means the server has acknowledged the event.
 * 'inProgress' shows the event is ongoing (e.g., typing or generating). 'final' marks the
 * event as complete with no further changes. 'evaluated' indicates the event has been
 * analyzed by AI for additional metrics.
 */
export enum EventStatus {
    Confirmed = "confirmed",
    Created = "created",
    Evaluated = "evaluated",
    Final = "final",
    InProgress = "inProgress",
}

/**
 * Describes a function that can be called and the conditions for calling it.
 */
export interface ScenarioFunction {
    /**
     * A human readable description of the function and when to call it.
     */
    description?: string;
    /**
     * The unique name of the function to call. This is the same as the function ID.
     */
    name?: string;
    /**
     * The ID of the next step to go to, if this function should also create a step change.
     */
    nextStep?: string;
    /**
     * The JSON schema of the parameters to pass to the function.
     */
    parameters?: { [key: string]: any };
    /**
     * For fallback purposes, if this pattern matches a response, trigger the function. Can be
     * an exact string or regex pattern (e.g., '/pattern/i') which is matched against common
     * fields in an incoming message or SessionEvent
     */
    pattern?: string;
}

/**
 * Optional session data to merge into any existing session data.
 */
export interface Session {
    /**
     * Flags whether the session has been anonymized or not.
     */
    anonymized?: boolean;
    appVersion?: string;
    /**
     * This session was generated from an assessment scenario and dialogue and ratings should be
     * hidden to player until reviewed.
     */
    assessmentSession?: boolean;
    /**
     * Time session was created as ISO8601 datetime
     */
    created:     string;
    evaluation?: { [key: string]: SessionRating };
    events?:     SessionEvent[];
    goalStats?:  GoalStats;
    /**
     * Object holding settings and identifiers for integrations relating to this session.
     */
    integrations?:   Integrations;
    locale?:         string;
    model?:          string;
    organizationId?: string;
    /**
     * Map of user IDs to their role in the session. Should but doesn't have to include the
     * player.
     */
    participants?:      { [key: string]: Participant };
    playerAvatar?:      string;
    playerDisplayName?: string;
    playerId:           string;
    /**
     * What status was the player in when this session started?
     */
    playerStatus?:        PlayerStatus;
    scenarioData?:        Scenario;
    scenarioDisplayName?: string;
    scenarioId:           string;
    /**
     * The type of the scenario at the time of the session
     */
    scenarioType?:  ScenarioType;
    serverVersion?: string;
    /**
     * Session length in ms
     */
    sessionLength?: number;
    settings?:      SettingsObject;
    /**
     * Map of user/org IDs that this session is shared with.
     */
    sharedWith?: { [key: string]: boolean };
    /**
     * A flat object where each key is a snake_case name for a stat or metric about the session.
     */
    stats?: { [key: string]: number };
    /**
     * Status of the session. started = session was started, played = session had at least one
     * PLAYER_LINE, ended = session was finished and ended by player, analyzed = session has
     * been evaluated by the AI / coach, feedbacked: = Session got feedback from a user (can be
     * other than player), rated = session was reviewed by the player pr another person after
     * ending, finalized = session has been marked as finalized (and e.g. shared back to users)
     */
    status?: SessionStatus;
    /**
     * Did the player use the tutorial before starting?
     */
    tutorialWatched?: boolean;
    /**
     * Time session was updated as ISO8601 datetime
     */
    updated?: string;
}

/**
 * Aggregated goal evaluation stats for a session.
 */
export interface GoalStats {
    /**
     * Average score across the session's goal evaluations.
     */
    averageScore?: number;
    /**
     * Count of each rating key across the session's goal evaluations.
     */
    keyCount?: { [key: string]: number };
    /**
     * Maximum number of goal evaluations that could have been present.
     */
    maxCount?: number;
}

/**
 * Object holding settings and identifiers for integrations relating to this session.
 */
export interface Integrations {
    learnifierReportId?: string;
    sessionToken?:       string;
    [property: string]: any;
}

export enum Participant {
    GameMaster = "gameMaster",
    Player = "player",
    Reviewer = "reviewer",
    Viewer = "viewer",
}

/**
 * What status was the player in when this session started?
 */
export enum PlayerStatus {
    FirstTimeOnPlatform = "firstTimeOnPlatform",
    FirstTimeOnScenario = "firstTimeOnScenario",
    RepeatPlayer = "repeatPlayer",
    ScenarioCreator = "scenarioCreator",
}

/**
 * The type of the scenario at the time of the session
 */
export enum ScenarioType {
    Published = "published",
    Unpublished = "unpublished",
}

/**
 * Status of the session. started = session was started, played = session had at least one
 * PLAYER_LINE, ended = session was finished and ended by player, analyzed = session has
 * been evaluated by the AI / coach, feedbacked: = Session got feedback from a user (can be
 * other than player), rated = session was reviewed by the player pr another person after
 * ending, finalized = session has been marked as finalized (and e.g. shared back to users)
 */
export enum SessionStatus {
    Analyzed = "analyzed",
    Ended = "ended",
    Feedbacked = "feedbacked",
    Finalized = "finalized",
    Played = "played",
    Rated = "rated",
    Started = "started",
}

export interface BotMessageResponse {
    connectInfo?: { [key: string]: any };
    /**
     * Status of the recognition process.
     */
    recognitionStatus?: RecognitionStatus;
    recognizedMessage?: SessionEvent;
    /**
     * Array of message objects from the bot.
     */
    responseMessages?: SessionEvent[];
    /**
     * Session ID
     */
    sessionId: string;
    [property: string]: any;
}

/**
 * Status of the recognition process.
 */
export enum RecognitionStatus {
    EndOfUtteranceTimeout = "endOfUtteranceTimeout",
    Error = "error",
    Final = "final",
    InitialSilenceTimeout = "initialSilenceTimeout",
    NoMatch = "noMatch",
    SpeechTooLong = "speechTooLong",
    Started = "started",
    Success = "success",
}

export interface CustomClaims {
    $schema?: string;
    /**
     * Objectmap version of memberOf. Identifiers that this user is considered an authorized
     * member of. Should be sorted so that the most common claim comes first.
     */
    has?: { [key: string]: boolean };
    /**
     * Identifiers that this user is considered an authorized member of. Should be sorted so
     * that the most common claim comes first.
     */
    memberOf?: string[];
}

/**
 * An invite to the platform to enter as an anonymous user.
 */
export interface Invite {
    description?: string;
    /**
     * When this invite link will expire. If empty, it never expires.
     */
    expires?: string;
    /**
     * The unique key for this invite. Only used internally, should not be saved to database.
     */
    key?: string;
    /**
     * When this invite was last used to login successfully.
     */
    lastUsed?: string;
    /**
     * The locale to display the app in when entering.
     */
    locale?: string;
    /**
     * Maximum number of users created with this invite.
     */
    maxUsers?: number;
    /**
     * The organization that invited users should belong to. Can also brand the invite link.
     */
    organization?: string;
    /**
     * The scenario that this invitation is associated with. Can be used to brand the invite
     * link or find the invitation again.
     */
    scenario?: string;
    /**
     * Number of successful users created with this invite.
     */
    users?: number;
}

export interface Organization {
    $schema?:    string;
    adminEmail?: string;
    /**
     * URL or Google Storage path to the cover image of the organization
     */
    coverImage?: string;
    displayName: string;
    domain:      string;
    /**
     * An ID string that can be found under /invites/:id
     */
    invite?: string;
    /**
     * A map of invite IDs created for this organization
     */
    invites?:                   { [key: string]: boolean };
    matchMakerId?:              number;
    scenarioOrganizationStats?: { [key: string]: ScenarioOrganizationStats };
    settings?:                  SettingsObject;
    stats?:                     OrganizationStats;
    /**
     * Stripe customer ID
     */
    stripeCustomerId?: string;
}

/**
 * Stats for a scenario based on the sessions played, i.e. if a user changes organization,
 * it will be registered anyway in these stats.
 */
export interface ScenarioOrganizationStats {
    /**
     * Number of unique users that have a session with this scenario and organization in the
     * last 30 days
     */
    activeUsers?: number;
    /**
     * Skill level from last session played of this scenario. Computation: average of the
     * averageLatestSkill under ScenarioUserStats (represents the skillLevel for last session
     * played) for each User having played this scenario.
     */
    averageLatestSkill?:   number;
    firstSessionDateTime?: string;
    lastSessionDateTime?:  string;
    /**
     * Total playtime in ms for all sessions within this organization and scenario.
     */
    playtimeTotal?: number;
    /**
     * Number of sessions that have been 'analyzed'
     */
    sessionsAnalyzed?: number;
    /**
     * Number of sessions that have been 'ended'
     */
    sessionsEnded?: number;
    /**
     * Number of sessions that have been 'feedbacked'
     */
    sessionsFeedbacked?: number;
    /**
     * Number of sessions that have been 'finalized'
     */
    sessionsFinalized?: number;
    /**
     * Number of sessions that have been 'played'
     */
    sessionsPlayed?: number;
    /**
     * Number of sessions that have been 'rated'
     */
    sessionsRated?: number;
    /**
     * Number of sessions that have been 'started', e.g. total number of sessions.
     */
    sessionsStarted?: number;
    [property: string]: any;
}

export interface OrganizationStats {
    /**
     * Number of unique users that have a session with this organization in the last 30 days
     */
    activeUsers?: number;
    /**
     * Average app recommendation evaluation for all sessions that got an evaluation, [0-1].
     * Counted by averageAppRecommend.
     */
    averageAppRecommend?: number;
    /**
     * Average bot performance evaluation for all sessions that got an evaluation, [0-1].
     * Counted by countBotPerformance.
     */
    averageBotPerformance?: number;
    /**
     * Average goal score for all sessions with this organization, [0-1]. Counted by
     * countGoalScore.
     */
    averageGoalScore?: number;
    /**
     * Average of all latest user skill levels for users in this organization, ignoring
     * undefined ones.
     */
    averageLatestSkill?: number;
    /**
     * Count occurrence for each app recommend score key (1, 2, 3, 4, 5.)
     */
    countAppRecommend?: { [key: string]: number };
    /**
     * Count occurrence for each bot performance score key (1, 2, 3, 4, 5.)
     */
    countBotPerformance?: { [key: string]: number };
    /**
     * Count which decile average goal score fall in (10%, 20%, 30%, 40%, 50%...)
     */
    countGoalScore?: { [key: string]: number };
    /**
     * Count of users in this org whose skill fall in (10%, 20%, 30%, 40%, 50%...)
     */
    countSkill?: { [key: string]: number };
    /**
     * The first time a session was played in this organization
     */
    firstSessionDateTime?: string;
    /**
     * The most recent time a session was played in this organization
     */
    lastSessionDateTime?: string;
    /**
     * Total playtime accumulated over sessions attributed to the organization in ms, regardless
     * if the user is still part of the organization
     */
    playtimeTotal?: number;
    /**
     * Number of published scenarios
     */
    publishedScenarios?: number;
    /**
     * Number of registered admins
     */
    registeredAdmins?: number;
    /**
     * Number of registered users
     */
    registeredUsers?: number;
    /**
     * Number of sessions from this organization that have been analyzed for goals and AI coach
     */
    sessionsAnalyzed?: number;
    /**
     * Number of sessions from this organization that ended normally, e.g. not aborted
     */
    sessionsEnded?: number;
    /**
     * Number of sessions from this organization that have received at least one user or admin
     * feedback, e.g. thumbs up/down
     */
    sessionsFeedbacked?: number;
    /**
     * Number of sessions from this organization that have been marked as finalized
     */
    sessionsFinalized?: number;
    /**
     * Number of sessions in this organization where the user has started speaking to the bot
     */
    sessionsPlayed?: number;
    /**
     * Number of sessions from this organization that have been marked as reviewed by an admin
     * or manager
     */
    sessionsRated?: number;
    /**
     * Number of sessions from this organization that has been started
     */
    sessionsStarted?: number;
    /**
     * Array of skill levels over time, e.g. for graphing. Should be kept at a maximum length by
     * removing less useful entries.
     */
    skillHistory?: FluffySkillHistory[];
    /**
     * Number of unpublished scenarios
     */
    unpublishedScenarios?: number;
    [property: string]: any;
}

export interface FluffySkillHistory {
    datetime: string;
    value:    number;
    [property: string]: any;
}

export interface SessionExternalUpdate {
    $schema?:    string;
    appVersion?: string;
    /**
     * This session was generated from an assessment scenario and dialogue and ratings should be
     * hidden to player unil reviewed.
     */
    assessmentSession?: boolean;
    /**
     * Time session was created as ISO8601 datetime
     */
    created:              string;
    evaluation?:          { [key: string]: Rating };
    goalStats?:           GoalStats;
    locale?:              string;
    organizationId?:      string;
    playerDisplayName?:   string;
    playerEmail?:         string;
    playerId:             string;
    scenarioDisplayName?: string;
    scenarioId:           string;
    sessionId?:           string;
    /**
     * Session length in ms
     */
    sessionLength?: number;
    sessionURL?:    string;
    /**
     * A flat object where each key is a snake_case name for a stat or metric about the session.
     */
    stats?: { [key: string]: number };
    /**
     * Status of the session. started = session was started, played = session had at least one
     * PLAYER_LINE, ended = session was finished and ended by player, analyzed = session has
     * been evaluated by the AI / coach, feedbacked: = Session got feedback from a user (can be
     * other than player), rated = session was reviewed by the player pr another person after
     * ending, finalized = session has been marked as finalized (and e.g. shared back to users)
     */
    status?: SessionStatus;
    /**
     * Time session was updated as ISO8601 datetime
     */
    updated?: string;
}

export interface Rating {
    /**
     * Custom data point to show that was used to deduce the rating, e.g pitch in Hz. Used for
     * confidence in STT.
     */
    dataPoint?: number;
    /**
     * Custom data text, can be used for e.g. transcribed text.
     */
    dataText?: string;
    /**
     * Key that identifies the rating, e.g. 'bad', 'ok' or 'good'
     */
    ratingKey?: string;
    /**
     * A score value to accompany the key, typically between 0.0 to 1.0. Used for WER in STT.
     */
    ratingScore?: number;
    [property: string]: any;
}

export interface User {
    $schema?: string;
    /**
     * Now means a unique identifier of the avatar. Either a new RPM style URL, or a shorthand
     * for preloaded avatar or a gender for default gendered avatar.
     */
    avatar?:              string;
    avatarClothingColor?: string;
    /**
     * Has been used for avatar identifier but this will be deprecated in favor of avatar.
     */
    avatarName?: string;
    /**
     * List of avatars created by this user.
     */
    createdAvatars?:    CreatedAvatar[];
    currentScenario?:   string;
    currentSession?:    string;
    displayName?:       string;
    email?:             string;
    organization?:      string;
    scenarioUserStats?: { [key: string]: ScenarioUserStats };
    settings?:          SettingsObject;
    /**
     * Each key represents a unique tag intended for filtering and sorting.
     */
    tags?:         { [key: string]: boolean };
    termsConsent?: boolean;
    /**
     * User ID from Firebase Auth
     */
    uid?: string;
    /**
     * Stats concerning the user
     */
    userStats?: UserStats;
}

export interface CreatedAvatar {
    id:   string;
    name: string;
}

export interface ScenarioUserStats {
    /**
     * If it has a date, it was archived at that time, otherwise not archived.
     */
    archived?: string;
    /**
     * Skill level from last session played of this scenario, undefined if the session scenario
     * had no goals
     */
    averageLatestSkill?:   number;
    exchangeStats?:        { [key: string]: ExchangeStats };
    firstSessionDateTime?: string;
    lastSessionDateTime?:  string;
    /**
     * Total playtime in ms for all sessions with this user and scenario.
     */
    playtimeTotal?: number;
    /**
     * Number of sessions that have been 'analyzed'
     */
    sessionsAnalyzed?: number;
    /**
     * Number of sessions that have been 'ended'
     */
    sessionsEnded?: number;
    /**
     * Number of sessions that have been 'feedbacked'
     */
    sessionsFeedbacked?: number;
    /**
     * Number of sessions that have been 'finalized'
     */
    sessionsFinalized?: number;
    /**
     * Number of sessions that have been 'played'
     */
    sessionsPlayed?: number;
    /**
     * Number of sessions that have been 'rated'
     */
    sessionsRated?: number;
    /**
     * Number of sessions that have been 'started', e.g. total number of sessions.
     */
    sessionsStarted?: number;
    /**
     * Array of skill levels over time, e.g. for graphing. Should be kept at a maximum length by
     * removing less useful entries.
     */
    skillHistory?: ScenarioUserStatSkillHistory[];
}

export interface ExchangeStats {
    bestInSession?: string;
    /**
     * The YYYY-MM-DD date when this exchange is due for practice
     */
    dueDate?: string;
    /**
     * A factor that is reduced if a user fails an Exchange or increases if a user succeeds.
     * This reflects that some exchanges are harder than others to recall.
     */
    easeFactor?: number;
    /**
     * Index in a table of intervals in days for next practice.
     */
    intervalIndex?: number;
    /**
     * Latest ratingKey for the rating 'score'
     */
    latestScore?:       string;
    presentInSessions?: string[];
    /**
     * Number of times this exchange has been responded to by player.
     */
    repetitions?: number;
}

export interface ScenarioUserStatSkillHistory {
    datetime: string;
    value:    number;
    [property: string]: any;
}

/**
 * Stats concerning the user
 */
export interface UserStats {
    /**
     * Average of skill levels from each scenarioStats of this user, ignoring undefined ones.
     */
    averageLatestSkill?: number;
    /**
     * Count which decile average skill fall in (10%, 20%, 30%, 40%, 50%...)
     */
    countSkill?: { [key: string]: number };
    /**
     * The first time a session was played by this user
     */
    firstSessionDateTime?: string;
    /**
     * The most recent time a session was played by this user
     */
    lastSessionDateTime?: string;
    /**
     * Total playtime in ms for all sessions with this user.
     */
    playtimeTotal?: number;
    /**
     * Number of sessions from this user that have been analyzed for goals and AI coach
     */
    sessionsAnalyzed?: number;
    /**
     * Number of sessions from this user that ended normally, e.g. not aborted
     */
    sessionsEnded?: number;
    /**
     * Number of sessions from this user that have received at least one user or admin feedback,
     * e.g. thumbs up/down
     */
    sessionsFeedbacked?: number;
    /**
     * Number of sessions from this user that have been marked as finalized
     */
    sessionsFinalized?: number;
    /**
     * Number of sessions where this user has started speaking to the bot
     */
    sessionsPlayed?: number;
    /**
     * Number of sessions from this user that have been marked as reviewed by an admin or manager
     */
    sessionsRated?: number;
    /**
     * Number of sessions from this user that has been started
     */
    sessionsStarted?: number;
    /**
     * Array of skill levels over time, e.g. for graphing. Should be kept at a maximum length by
     * removing less useful entries.
     */
    skillHistory?: UserStatsSkillHistory[];
    [property: string]: any;
}

export interface UserStatsSkillHistory {
    datetime: string;
    value:    number;
    [property: string]: any;
}

export interface Voice {
    /**
     * The provider-specific configuration for this voice.
     */
    config?: { [key: string]: any };
    /**
     * The JSON schema for the config object supported by this voice.
     */
    configSchema?: { [key: string]: any };
    /**
     * The cost in USD per unit of text or time.
     */
    costPerUnit?: number;
    /**
     * The unit for costPerUnit, e.g. '1k chars' or '1 min audio'.
     */
    costUnit?: string;
    /**
     * Whether this voice is deprecated and should not be used for new synthesis requests.
     */
    deprecated?:  boolean;
    description?: string;
    /**
     * Whether this voice supports expressing emotes / emotions, like laughter.
     */
    emotes?: boolean;
    /**
     * Whether this voice is served from EU and e.g. is GDPR safe.
     */
    euBased?: boolean;
    /**
     * The genders that this voice can be used for.
     */
    genders?: string[];
    /**
     * The expected latency of this voice.
     */
    latency?: string;
    /**
     * The lipsync format supported by this voice, if any.
     */
    lipsync?: string;
    /**
     * The locales that this voice can change between. Assumed in order of preference, and
     * typically contains just one locale.
     */
    locales?: string[];
    /**
     * The name of the voice.
     */
    name?: string;
    /**
     * The pitch adjustment for this voice, if supported by the provider. Supported range
     * depends on provider.
     */
    pitch?: number;
    /**
     * Whether this voice can be prompted to change how it speaks.
     */
    promptable?: boolean;
    /**
     * Whether this voice supports custom pronunciation lexicons.
     */
    pronounciation?: boolean;
    provider:        string;
    /**
     * The rate adjustment for this voice, if supported by the provider.  Supported range
     * depends on provider.
     */
    rate?: number;
    /**
     * Whether this voice is recommended for use.
     */
    recommended?: boolean;
    /**
     * Tags associated with this voice.
     */
    tags?: string[];
    /**
     * The provider specific ID of this voice. voice_name for Azure and Google, voice_id for
     * ElevenLabs, speaker for Rime, etc
     */
    voiceId: string;
    /**
     * Whether this voice streams sound and transcripts word-by-word.
     */
    wordByWord?: boolean;
    [property: string]: any;
}
